import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Lock, Sparkles } from "lucide-react";
import { API } from "@/lib/auth";
import { Skel } from "@/components/market/Shared";
import { cents, endsIn } from "./utils";

const PickCard = ({ p, onOpen }) => (
  <button onClick={() => onOpen(p.slug)} data-testid={`pick-${p.slug}`}
    className="glass p-4 text-left hover:border-[var(--cyan)]/60 transition-colors flex flex-col gap-2 min-w-0">
    <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider">
      <span className="text-slate-500">{p.group}</span>
      <span className={`px-2 py-0.5 rounded-full font-bold ${p.strength === "Strong favourite" ? "bg-emerald-500/15 text-emerald-300" : "bg-[var(--cyan)]/10 text-[var(--cyan)]"}`}>{p.strength}</span>
    </div>
    <div className="text-sm font-semibold line-clamp-2">{p.label}</div>
    <div className="flex items-end justify-between gap-2">
      <div><div className="text-[11px] text-slate-500">Data pick</div><div className="font-head font-bold text-lg truncate">{p.pick_name}</div></div>
      <div className="text-right font-num"><div className="text-xl font-semibold">{Math.round(p.chance * 100)}%</div><div className="text-[11px] text-slate-500">pay {cents(p.pay_price)} · {endsIn(p)}</div></div>
    </div>
    <ul className="text-[11px] text-slate-400 space-y-0.5">{p.reasons.slice(1).map((r) => <li key={r}>· {r}</li>)}</ul>
    <div className="text-[11px] text-amber-200/80">Break-even win rate {p.breakeven_pct}%</div>
  </button>
);

export const PicksStrip = ({ category, onOpen }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    axios.get(`${API}/predict/picks`, { params: { category } }).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [category]);
  if (!d) return <Skel h="h-48" />;
  if (d.error) return null;
  const groups = Object.values(d.categories);
  const list = category === "all" ? groups.flatMap((g) => g.picks.slice(0, 1)) : groups[0]?.picks || [];
  const locked = groups.reduce((a, g) => a + g.locked, 0);
  return (
    <section className="space-y-3" data-testid="picks-strip">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-head font-semibold text-lg flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--violet)]" />Data picks</h2>
        <span className="text-[11px] text-slate-500">{d.tier === "free" ? "Free" : d.tier === "pro" ? "Pro" : "Elite"} · {d.per_category} per category</span>
      </div>
      {list.length === 0 ? <p className="glass p-4 text-sm text-slate-400" data-testid="picks-empty">No market meets the data-pick rules right now. That's normal — we'd rather show nothing than a weak pick.</p> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{list.map((p) => <PickCard key={p.slug} p={p} onOpen={onOpen} />)}</div>}
      {locked > 0 && d.tier !== "elite" && (
        <Link to="/pro" className="glass p-3 flex items-center gap-2 text-sm text-[var(--violet)] hover:border-[var(--violet)]" data-testid="picks-upsell">
          <Lock className="w-4 h-4" />{locked} more picks{d.tier === "free" ? " + 1-hour price movement" : ""} with {d.tier === "free" ? "Pro or Elite" : "Elite"}</Link>)}
      <p className="text-[11px] text-slate-500" data-testid="picks-method">{d.method}</p>
    </section>
  );
};
